import CancelButton from "Common/Components/Buttons/CancelButton";
import PrimarySubmitButton from "Common/Components/Buttons/PrimarySubmitButton";
import LoadingSpinner from "Common/Components/LoaderErrorWrapper/LoadingSpinner";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  addCategory,
  fetchCategoryById,
  updateCategory,
} from "slices/Masters/category/thunk";
import * as Yup from "yup";
import { clearTableState } from "helpers/ClearTableState/ClearTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { useScrollToFormikError } from "hooks/useScrollToFormikError";

const CategoryForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [initialData, setInitialData] = useState({
    categoryName: "",
  });
  const dispatch = useDispatch<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (isEdit && id) {
      setIsLoading(true);
      dispatch(fetchCategoryById(id))
        .then((res: any) => {
          let fetchedData = res?.payload?.category_name || " ";
          setInitialData({ categoryName: fetchedData });
        })
        .catch((err: any) => {
          toast.error("Failed to load category details");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id, isEdit]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialData,
    validationSchema: Yup.object({
      categoryName: Yup.string().trim().required("Please enter category name"),
      // .matches(
      //   /^[A-Za-z\s-]+$/,
      //   "Category name must contain only letters, spaces, and hyphens"
      // ),
    }),
    onSubmit: async (values) => {
      const payload = {
        category_name: values.categoryName.trim(),
      };
      setIsSubmitting(true);
      try {
        if (isEdit) {
          await dispatch(updateCategory({ id, ...payload })).unwrap();
          toast.success("Category updated successfully");
        } else {
          await dispatch(addCategory(payload)).unwrap();
          clearTableState(TableKeys.MASTER.CATEGORY);
          toast.success("Category added successfully");
        }
        navigate("/master/category");
      } catch (error: any) {
        console.error("Add category error:", error);
        toast.error(error?.message || "Failed to add category");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  useScrollToFormikError(formik.errors, formik.submitCount, formik.isSubmitting);

  return (
    <PageContainer>
      <PageBreadcrumb
        title={id ? "Edit Category" : "Add Category"}
        titleLink="/master/category"
        breadcrumbs={[
          { label: "Category", to: "/master/category" },
          { label: id ? "Edit Category" : "Add Category", to: "" },
        ]}
        backNavigationPath={-1}
      />
      <hr />

      {isLoading && isEdit ? (
        <LoadingSpinner />
      ) : (
        <Form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <Row className="mb-3">
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">
                Category Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                name="categoryName"
                placeholder="Enter Category Name"
                value={formik.values.categoryName}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={
                  !!formik.errors.categoryName && formik.touched.categoryName
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.categoryName}
              </Form.Control.Feedback>
            </Col>
          </Row>

          {/* Buttons */}
          <div className="d-flex flex-column flex-md-row justify-content-center gap-3 mt-3">
            <CancelButton
              redirectTo="/master/category"
              disabled={isSubmitting}
            />
            <PrimarySubmitButton
              label={id ? "Update" : "Submit"}
              isLoading={isSubmitting}
              loadingLabel={id ? "Updating..." : "Submitting..."}
            />
          </div>
        </Form>
      )}
    </PageContainer>
  );
};

export default CategoryForm;
